"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

// ---- Latency comparison: Photon vs other VLMs (lower is better) ----
const BENCH = [
  { label: "Moondream 3 Preview + Photon", ms: 34, note: "H100 · batch 1", emphasis: true },
  { label: "Qwen 3.5 4B + vLLM", ms: 73, note: "H100 · batch 1", emphasis: false },
  { label: "GPT-5.4 Mini", ms: 2780, note: "OpenAI API", emphasis: false },
  { label: "Gemini 2.5 Flash", ms: 3790, note: "Google API", emphasis: false },
];

const AXIS_BREAK_START_MS = 500;
const AXIS_BREAK_END_MS = 2500;
const AXIS_BREAK_POSITION = 46;

const getBrokenAxisPercent = (ms: number, maxMs: number) => {
  if (ms <= AXIS_BREAK_START_MS) {
    return (ms / AXIS_BREAK_START_MS) * AXIS_BREAK_POSITION;
  }

  if (ms < AXIS_BREAK_END_MS) {
    return AXIS_BREAK_POSITION;
  }

  const postBreakRange = maxMs - AXIS_BREAK_END_MS;
  if (postBreakRange <= 0) return AXIS_BREAK_POSITION;

  const postBreakPercent = Math.max(0, ms - AXIS_BREAK_END_MS) / postBreakRange;
  return AXIS_BREAK_POSITION + postBreakPercent * (100 - AXIS_BREAK_POSITION);
};

export const BenchmarkChart = () => {
  const fastest = Math.min(...BENCH.map((b) => b.ms));
  const slowest = Math.max(...BENCH.map((b) => b.ms));
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [started, setStarted] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [filling, setFilling] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Trigger first run when the card scrolls into view
  useEffect(() => {
    if (!cardRef.current) return;
    const el = cardRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setStarted(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Animation loop: snap-to-empty → fill (each bar takes its own `ms`) → hold 3s → repeat.
  // Bars start together; each finishes when its real-world latency would have elapsed,
  // so the fastest model "wins the race" first.
  useEffect(() => {
    if (!started) return;
    setFilling(false);
    setElapsedMs(0);
    // Wait one paint so the empty state commits before we kick off the transition.
    let raf = 0;
    let startTime = 0;
    const tick = (time: number) => {
      if (!startTime) startTime = time;
      const nextElapsed = Math.min(time - startTime, slowest);
      setElapsedMs(nextElapsed);
      if (nextElapsed < slowest) raf = requestAnimationFrame(tick);
    };
    const t1 = setTimeout(() => {
      setFilling(true);
      raf = requestAnimationFrame(tick);
    }, 50);
    // After the slowest bar finishes plus a 3s pause, restart the cycle.
    const t2 = setTimeout(() => setCycle((c) => c + 1), 50 + slowest + 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(raf);
    };
  }, [started, cycle, slowest]);

  return (
    <div className="bench-card" ref={cardRef}>
      <div className="bench-head">
        <div>
          <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
            Internal benchmark
          </div>
          <div className="t-heading-md" style={{ marginTop: 4 }}>
            Time per request
          </div>
        </div>
        <div className="bench-controls">
          <span
            className="mono"
            style={{ fontSize: 11, color: "var(--text-tertiary)" }}
          >
            median of 200 runs
          </span>
        </div>
      </div>
      <div className="bench-plot">
        <div className="bench-rows">
          {BENCH.map((b) => {
            const currentMs = filling ? Math.min(elapsedMs, b.ms) : 0;
            const pct = getBrokenAxisPercent(currentMs, slowest);
            const mult = b.ms / fastest;
            return (
              <div
                className={`bench-row ${b.emphasis ? "emph" : ""}`}
                key={b.label}
              >
                <div className="bench-label">
                  <span className="bench-name">{b.label}</span>
                  <span className="bench-note mono">{b.note}</span>
                </div>
                <div className="bench-bar-wrap">
                  <span
                    className="bench-axis-break"
                    aria-hidden="true"
                    title="Axis break: 0.5s to 2.5s skipped"
                  />
                  <div
                    className="bench-bar"
                    style={{
                      width: `${pct}%`,
                    }}
                  />
                </div>
                <div className="bench-value tabular mono">
                  <span className="bench-num">
                    {b.ms >= 1000 ? (b.ms / 1000).toFixed(2) : b.ms.toLocaleString()}
                  </span>
                  <span className="bench-unit">{b.ms >= 1000 ? " s" : " ms"}</span>
                  {!b.emphasis && (
                    <span className="bench-mult">
                      {mult >= 10 ? Math.round(mult) : mult.toFixed(1)}× slower
                    </span>
                  )}
                  {b.emphasis && (
                    <span
                      className="bench-mult"
                      style={{ color: "var(--accent-text)" }}
                    >
                      baseline
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="bench-annot" aria-hidden="true">
          <svg
            className="bench-annot-arrow"
            width="16"
            height="28"
            viewBox="0 0 16 28"
            fill="none"
          >
            {/* Same semi-circle as before, rotated 90° CW: chord is now
                vertical (left side), arc bulges right, tip at the bottom-left
                with the tangent pointing horizontally to the left. */}
            <path
              d="M 2 2 A 10 10 0 0 1 2 22"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M 2 22 L 6 19 M 2 22 L 6 25"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
          <span className="bench-annot-text">shorter is better!</span>
        </div>
      </div>
      <div className="bench-foot">
        <span
          className="mono"
          style={{ fontSize: 11, color: "var(--text-tertiary)" }}
        >
          1920×1080 input · single-turn detect · NVIDIA H100 80GB · 2026-02 build
        </span>
      </div>
    </div>
  );
};

// ---- Hardware icons ----
const HwIconBase = ({
  children,
  size = 28,
}: {
  children: ReactNode;
  size?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

type HwIconProps = { size?: number };

const HwIconJetson = (p: HwIconProps) => (
  <HwIconBase {...p}>
    <rect x="5" y="10" width="22" height="14" rx="1.5" />
    <path d="M8 10V6M12 10V6M16 10V6M20 10V6M24 10V6" />
    <rect x="9" y="14" width="14" height="6" rx="0.5" />
    <circle cx="12" cy="17" r="0.8" fill="currentColor" />
    <circle cx="20" cy="17" r="0.8" fill="currentColor" />
  </HwIconBase>
);
const HwIconL4 = (p: HwIconProps) => (
  <HwIconBase {...p}>
    <rect x="3" y="13" width="26" height="6" rx="0.5" />
    <path d="M7 15v2M10 15v2M13 15v2M16 15v2M19 15v2M22 15v2M25 15v2" />
    <rect x="3" y="10" width="3" height="3" />
  </HwIconBase>
);
const HwIconAmpere = (p: HwIconProps) => (
  <HwIconBase {...p}>
    <rect x="3" y="11" width="26" height="10" rx="1" />
    <circle cx="9" cy="16" r="2.5" />
    <path d="M9 13.5v5M6.5 16h5" />
    <path d="M15 14h10M15 16h10M15 18h10" />
  </HwIconBase>
);
const HwIconL40 = (p: HwIconProps) => (
  <HwIconBase {...p}>
    <rect x="3" y="12" width="26" height="8" rx="0.5" />
    <path d="M6 14v4M9 14v4M12 14v4M15 14v4M18 14v4M21 14v4M24 14v4" />
    <rect x="3" y="9" width="3" height="3" />
  </HwIconBase>
);
const HwIconRack = (p: HwIconProps) => (
  <HwIconBase {...p}>
    <rect x="5" y="5" width="22" height="22" rx="1.5" />
    <path d="M5 11h22M5 17h22M5 23h22" />
    <circle cx="9" cy="8" r="0.7" fill="currentColor" />
    <circle cx="11.5" cy="8" r="0.7" fill="currentColor" />
    <circle cx="9" cy="14" r="0.7" fill="currentColor" />
    <circle cx="11.5" cy="14" r="0.7" fill="currentColor" />
    <circle cx="9" cy="20" r="0.7" fill="currentColor" />
    <circle cx="11.5" cy="20" r="0.7" fill="currentColor" />
    <path d="M16 7.5h8M16 13.5h8M16 19.5h8" />
  </HwIconBase>
);

type HwTile = {
  name: string;
  tier: string;
  ms: number;
  rps: number;
  model: string;
  Icon: (p: HwIconProps) => ReactNode;
  featured?: boolean;
};

const HARDWARE: HwTile[] = [
  { name: "Jetson AGX Orin", tier: "Edge · 32 GB", ms: 543, rps: 3.66, model: "Moondream 2", Icon: HwIconJetson },
  { name: "NVIDIA L4", tier: "Workstation", ms: 358, rps: 4.85, model: "Moondream 3", Icon: HwIconL4 },
  { name: "NVIDIA A10", tier: "Cloud · Ampere", ms: 223, rps: 6.83, model: "Moondream 2", Icon: HwIconAmpere },
  { name: "NVIDIA A100 80GB", tier: "Cloud · Ampere", ms: 104, rps: 21.36, model: "Moondream 2", Icon: HwIconAmpere },
  { name: "NVIDIA L40S", tier: "Server", ms: 121, rps: 18.81, model: "Moondream 3", Icon: HwIconL40 },
  { name: "NVIDIA H100", tier: "Server · Hopper", ms: 62, rps: 58.01, model: "Moondream 3", Icon: HwIconRack, featured: true },
];

export const HardwareRange = () => {
  return (
    <div className="hw-panel">
      <div className="hw-panel-head">
        <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
          Photon · hardware
        </div>
        <div className="t-heading-md" style={{ marginTop: 4 }}>
          Same model, every tier.
        </div>
        <p
          className="t-body-md"
          style={{
            color: "var(--text-secondary)",
            marginTop: 8,
            maxWidth: 620,
          }}
        >
          Measured on the ChartQA test split with prefix caching enabled.
          Latency is the P50 of a single direct-answer{" "}
          <span className="mono" style={{ fontSize: "0.9em" }}>
            query
          </span>{" "}
          call; throughput is sustained requests per second at batch 64.
        </p>
      </div>
      <div className="hw-grid">
        {HARDWARE.map((h) => (
          <div
            key={h.name}
            className={`hw-tile ${h.featured ? "featured" : ""}`}
          >
            <div className="hw-tile-icon">
              <h.Icon size={28} />
            </div>
            <div className="hw-tile-name">{h.name}</div>
            <div className="hw-tile-tier mono">
              {h.tier} · {h.model}
            </div>
            <div className="hw-tile-metrics">
              <div className="hw-metric">
                <div className="hw-metric-val">
                  <span className="hw-metric-num tabular mono">{h.ms}</span>
                  <span className="hw-metric-unit mono">ms</span>
                </div>
                <div className="hw-metric-k mono">P50 · batch 1</div>
              </div>
              <div className="hw-metric-div" />
              <div className="hw-metric">
                <div className="hw-metric-val">
                  <span className="hw-metric-num tabular mono">{h.rps}</span>
                  <span className="hw-metric-unit mono">req/s</span>
                </div>
                <div className="hw-metric-k mono">sustained · batch 64</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="hw-foot mono">
        Source: kestrel/PERFORMANCE.md · Moondream 3 requires sm89+, so Ampere
        parts (A100, A10, Jetson AGX Orin) report Moondream 2.
      </div>
    </div>
  );
};

export const PartnerClouds = () => {
  const partners: { name: string; status: "live" | "soon" }[] = [
    { name: "FAL", status: "live" },
    { name: "Self-hosted", status: "live" },
    { name: "Moondream Cloud", status: "live" },
  ];
  return (
    <div className="partners-row">
      {partners.map((p) => (
        <div className={`partner-chip ${p.status}`} key={p.name}>
          <span className="partner-dot" />
          <span className="partner-name">{p.name}</span>
          {p.status === "soon" && (
            <span className="partner-soon">coming</span>
          )}
        </div>
      ))}
    </div>
  );
};
